import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ArrowLeft, Bell, Volume2, Shield, HelpCircle, Info, ChevronRight, Zap, Music, LogOut, Trash2, AlertTriangle, X, Globe } from 'lucide-react';
import { cn } from '../lib/utils';
import { useUser } from '../hooks/useUser';
import { useTranslation } from 'react-i18next';
import { audioEngine } from '../utils/audioEngine';
import { auth, logout, db } from '../firebase';
import { deleteUser } from 'firebase/auth';
import { doc, deleteDoc } from 'firebase/firestore';

interface SettingsProps {
  onBack: () => void;
}

export default function Settings({ onBack }: SettingsProps) {
  const { user, deleteAccount } = useUser();
  const { t, i18n } = useTranslation();
  const [sound, setSound] = useState(audioEngine.sfxEnabled);
  const [music, setMusic] = useState(audioEngine.musicEnabled);
  const [notifications, setNotifications] = useState(true);
  const [vibration, setVibration] = useState(true);
  const [activeContentView, setActiveContentView] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleSoundChange = (val: boolean) => {
    setSound(val);
    audioEngine.setSfxEnabled(val);
  };

  const handleMusicChange = (val: boolean) => {
    setMusic(val);
    audioEngine.setMusicEnabled(val);
  };

  const handleLogout = async () => {
    audioEngine.playSound('click');
    setShowLogoutConfirm(false);
    await logout();
  };

  const handleDeleteAccount = async () => {
    if (!auth.currentUser) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await deleteAccount();
    } catch (err: any) {
      if (err.code === 'auth/requires-recent-login') {
        setDeleteError(t('settings.delete_confirm.security_error'));
      } else {
        setDeleteError(err.message || t('settings.delete_confirm.general_error'));
      }
      setIsDeleting(false);
    }
  };

  const sections = [
    {
      title: t('settings.gameplay'),
      items: [
        { label: t('settings.sound_effects'), icon: Volume2, value: sound, onChange: handleSoundChange, type: 'toggle', color: 'text-blue-500 bg-blue-50' },
        { label: t('settings.background_music'), icon: Music, value: music, onChange: handleMusicChange, type: 'toggle', color: 'text-cyan-500 bg-cyan-50' },
        { label: t('settings.vibration'), icon: Zap, value: vibration, onChange: setVibration, type: 'toggle', color: 'text-orange-500 bg-orange-50' },
        { label: t('settings.language'), icon: Globe, type: 'select', color: 'text-emerald-500 bg-emerald-50' },
      ]
    },
    {
      title: t('settings.notifications'),
      items: [
        { label: t('settings.heart_restored'), icon: Bell, value: notifications, onChange: setNotifications, type: 'toggle', color: 'text-red-500 bg-red-50' },
      ]
    },
    {
      title: t('settings.account'),
      items: [
        { label: t('settings.privacy_policy'), icon: Shield, type: 'link', color: 'text-green-500 bg-green-50', onClick: () => setActiveContentView('privacy') },
        { label: t('settings.help_support'), icon: HelpCircle, type: 'link', color: 'text-purple-500 bg-purple-50', onClick: () => setActiveContentView('help') },
        { label: t('settings.about_game'), icon: Info, type: 'link', color: 'text-slate-500 bg-slate-50', onClick: () => setActiveContentView('about') },
      ]
    }
  ];

  const getHeaderTitle = () => {
    if (activeContentView === 'privacy') return t('settings.privacy_policy');
    if (activeContentView === 'help') return t('settings.help_support');
    if (activeContentView === 'about') return t('settings.about_game');
    return t('settings.title');
  };

  const renderContentView = () => {
    switch (activeContentView) {
      case 'privacy':
        return (
          <div className="space-y-4">
            <h2 className="text-2xl font-num text-slate-900 mb-6 uppercase">{t('settings.privacy_policy')}</h2>
            <section className="space-y-2">
              <h3 className="text-[#0ea5e9] font-black text-xs uppercase tracking-widest">{t('settings.privacy_content.collection_title')}</h3>
              <p className="text-slate-500 text-xs leading-relaxed">{t('settings.privacy_content.collection_text')}</p>
            </section>
            <section className="space-y-2">
              <h3 className="text-[#0ea5e9] font-black text-xs uppercase tracking-widest">{t('settings.privacy_content.analytics_title')}</h3>
              <p className="text-slate-500 text-xs leading-relaxed">{t('settings.privacy_content.analytics_text')}</p>
            </section>
          </div>
        );
      case 'help':
        return (
          <div className="space-y-4">
            <h2 className="text-2xl font-num text-slate-900 mb-6 uppercase">{t('settings.help_support')}</h2>
            <section className="space-y-2">
              <h3 className="text-[#0ea5e9] font-black text-xs uppercase tracking-widest">{t('settings.help_content.hearts_title')}</h3>
              <p className="text-slate-500 text-xs leading-relaxed">{t('settings.help_content.hearts_text')}</p>
            </section>
            <section className="space-y-2">
              <h3 className="text-rose-500 font-black text-xs uppercase tracking-widest">{t('settings.help_content.refund_title')}</h3>
              <p className="text-slate-500 text-xs leading-relaxed italic">{t('settings.help_content.refund_text')}</p>
            </section>
            <section className="space-y-2">
              <h3 className="text-[#0ea5e9] font-black text-xs uppercase tracking-widest">{t('settings.help_content.lost_title')}</h3>
              <p className="text-slate-500 text-xs leading-relaxed">{t('settings.help_content.lost_text')}</p>
            </section>
            <button className="w-full bg-slate-100 py-3 rounded-xl border-none text-xs font-black uppercase tracking-widest mt-4 text-slate-600 toy-3d-button">{t('settings.help_content.contact_btn')}</button>
          </div>
        );
      case 'about':
        return (
          <div className="space-y-4 flex flex-col items-center">
            <div className="text-2xl font-num text-slate-900 mb-4 tracking-tighter">NUM<span className="text-[#f43f5e]">Q</span></div>
            <p className="text-slate-500 text-[11px] leading-relaxed italic text-center">{t('settings.about_content.desc')}</p>
            <div className="bg-slate-50 p-4 rounded-2xl border-none">
              <div className="flex justify-between items-center mb-2">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('settings.about_content.engine')}</span>
                <span className="text-[10px] font-black text-slate-900">Vite / React</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('settings.about_content.version')}</span>
                <span className="text-[10px] font-black text-slate-900">1.2.7 r2027</span>
              </div>
            </div>
          </div>
        );
      default: return null;
    }
  };

  return (
    <div className="flex flex-col h-full bg-[#f8fafc] text-slate-900 p-6 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-64 h-64 bg-slate-200/50 rounded-full blur-3xl -mr-32 -mt-32" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-sky-100/50 rounded-full blur-3xl -ml-32 -mb-32" />

      {/* Header */}
      <div className="flex items-center gap-4 mb-8 relative z-10">
        <button onClick={activeContentView ? () => setActiveContentView(null) : onBack} className="p-3 bg-white rounded-2xl toy-3d-button border-none">
          <ArrowLeft className="w-6 h-6 text-slate-600" />
        </button>
        <h1 className="text-3xl font-num font-black tracking-tight text-slate-900 uppercase truncate">{getHeaderTitle()}</h1>
      </div>

      <div className="flex-1 overflow-y-auto pr-2 no-scrollbar space-y-8 relative z-10">
        {activeContentView ? (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white p-6 rounded-[32px] toy-3d-button border-none"
          >
            {renderContentView()}
          </motion.div>
        ) : (
          <>
            {sections.map((section, i) => (
              <div key={i}>
                <h3 className="text-xs font-black text-slate-400 uppercase tracking-[0.2em] mb-4 px-2">{section.title}</h3>
                <div className="space-y-3">
                  {section.items.map((item, j) => (
                    <button
                      key={j}
                      onClick={item.type === 'link' ? item.onClick : (item.type === 'select' ? undefined : () => {
                        audioEngine.playSound('click');
                        item.onChange?.(!item.value);
                      })}
                      className={cn(
                        "w-full bg-white p-4 rounded-3xl toy-3d-button border-none flex items-center justify-between group transition-all text-left",
                        item.type === 'select' && "cursor-default"
                      )}
                    >
                      <div className="flex items-center gap-4">
                        <div className={cn("p-3 rounded-2xl", item.color)}>
                          <item.icon className="w-6 h-6" />
                        </div>
                        <span className="font-black text-sm text-slate-700 uppercase tracking-tight">{item.label}</span>
                      </div>
                      {item.type === 'toggle' ? (
                        <div className={cn(
                          "w-14 h-8 rounded-full px-1 flex items-center transition-colors duration-300 relative border-2 border-slate-100",
                          item.value ? "bg-green-500 border-green-500" : "bg-slate-200"
                        )}>
                          <motion.div animate={{ x: item.value ? 20 : 0 }} className="w-6 h-6 bg-white rounded-full shadow-sm" />
                        </div>
                      ) : (
                        item.type === 'select' ? (
                          <select 
                            value={i18n.language} 
                            onChange={async (e) => {
                              audioEngine.playSound('click');
                              await i18n.changeLanguage(e.target.value);
                            }}
                            className="bg-slate-50 border-none font-black text-xs text-slate-700 rounded-xl px-3 py-1.5 focus:outline-none clay-base cursor-pointer"
                          >
                            <option value="en">English</option>
                            <option value="ko">한국어</option>
                            <option value="ja">日本語</option>
                            <option value="zh-CN">中文</option>
                            <option value="hi">हिन्दी</option>
                          </select>
                        ) : (
                          <ChevronRight className="w-5 h-5 text-slate-300" />
                        )
                      )}
                    </button>
                  ))}
                </div>
              </div>
            ))}

            {/* Session Section */}
            <div>
              <h3 className="text-xs font-black text-slate-400 uppercase tracking-[0.2em] mb-4 px-2">{t('settings.session')}</h3>
              <div className="space-y-3">
                <button
                  onClick={() => { audioEngine.playSound('click'); setShowLogoutConfirm(true); }}
                  className="w-full bg-white p-4 rounded-3xl toy-3d-button border-none flex items-center gap-4 text-left"
                >
                  <div className="p-3 rounded-2xl bg-slate-100 text-slate-500">
                    <LogOut className="w-6 h-6" />
                  </div>
                  <span className="font-black text-sm text-slate-700 uppercase tracking-tight">{t('settings.log_out')}</span>
                </button>

                <button
                  onClick={() => { audioEngine.playSound('click'); setShowDeleteConfirm(true); }}
                  className="w-full bg-rose-50 p-4 rounded-3xl border-2 border-rose-100 flex items-center gap-4 text-left active:scale-95 transition-all"
                >
                  <div className="p-3 rounded-2xl bg-rose-100 text-rose-500">
                    <Trash2 className="w-6 h-6" />
                  </div>
                  <span className="font-black text-sm text-rose-600 uppercase tracking-tight">{t('settings.delete_account')}</span>
                </button>
              </div>
            </div>

            <div className="pt-4 pb-8 text-center text-[10px] font-black text-slate-300 uppercase tracking-widest">
              {t('settings.version')}
            </div>
          </>
        )}
      </div>

      {/* Delete Account Confirmation Modal */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 z-[2000] flex items-center justify-center p-6"
          >
            <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm" onClick={() => !isDeleting && setShowDeleteConfirm(false)} />
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-white rounded-[40px] w-full max-w-sm p-8 relative z-10 shadow-2xl overflow-hidden"
            >
              {/* Red accent bar */}
              <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-rose-400 to-red-500" />

              <button
                onClick={() => { setShowDeleteConfirm(false); setDeleteError(null); }}
                disabled={isDeleting}
                className="absolute top-5 right-5 p-2 text-slate-300 hover:text-slate-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-rose-50 rounded-3xl flex items-center justify-center mb-5 border-2 border-rose-100">
                  <AlertTriangle className="w-8 h-8 text-rose-500" />
                </div>

                <h3 className="text-2xl font-num font-black uppercase text-slate-900 mb-2">{t('settings.delete_account')}</h3>
                <p className="text-xs text-slate-500 font-medium leading-relaxed mb-2">
                  {t('settings.delete_confirm.message')}
                </p>
                <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest mb-6">{t('settings.delete_confirm.warning')}</p>

                {deleteError && (
                  <div className="w-full bg-rose-50 border border-rose-200 rounded-2xl p-3 mb-4 text-left">
                    <p className="text-[10px] text-rose-600 font-bold leading-relaxed">{deleteError}</p>
                  </div>
                )}

                <div className="w-full space-y-3">
                  <button
                    onClick={handleDeleteAccount}
                    disabled={isDeleting}
                    className="w-full py-4 bg-rose-500 text-white font-black uppercase tracking-widest rounded-2xl toy-3d-button border-none disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {isDeleting ? (
                      <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> {t('settings.delete_confirm.deleting')}</>
                    ) : (
                      <><Trash2 className="w-4 h-4" /> {t('settings.delete_confirm.confirm')}</>
                    )}
                  </button>
                  <button
                    onClick={() => { setShowDeleteConfirm(false); setDeleteError(null); }}
                    disabled={isDeleting}
                    className="w-full py-4 bg-slate-100 text-slate-600 font-black uppercase tracking-widest rounded-2xl toy-3d-button border-none"
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Log Out Confirmation Modal */}
      <AnimatePresence>
        {showLogoutConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 z-[2000] flex items-center justify-center p-6"
          >
            <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm" onClick={() => setShowLogoutConfirm(false)} />
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-white rounded-[40px] w-full max-w-sm p-8 relative z-10 shadow-2xl overflow-hidden"
            >
              {/* Blue accent bar */}
              <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-sky-400 to-blue-500" />

              <button
                onClick={() => setShowLogoutConfirm(false)}
                className="absolute top-5 right-5 p-2 text-slate-300 hover:text-slate-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-blue-50 rounded-3xl flex items-center justify-center mb-5 border-2 border-blue-100">
                  <LogOut className="w-8 h-8 text-blue-500" />
                </div>

                <h3 className="text-2xl font-num font-black uppercase text-slate-900 mb-2">{t('settings.log_out')}</h3>
                <p className="text-xs text-slate-500 font-medium leading-relaxed mb-6">
                  {t('settings.logout_confirm.message')}
                </p>

                <div className="w-full space-y-3">
                  <button
                    onClick={handleLogout}
                    className="w-full py-4 bg-blue-500 text-white font-black uppercase tracking-widest rounded-2xl toy-3d-button border-none flex items-center justify-center gap-2"
                  >
                    <LogOut className="w-4 h-4" /> {t('settings.logout_confirm.confirm')}
                  </button>
                  <button
                    onClick={() => setShowLogoutConfirm(false)}
                    className="w-full py-4 bg-slate-100 text-slate-600 font-black uppercase tracking-widest rounded-2xl toy-3d-button border-none"
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
